import type { StackGap } from "../components/Stack/Stack.types";

/**
 * Takes in a gap size in either a CSS-style format (e.g. 10 or "10px")
 * Returns the separate numerical value of the padding (e.g. 10)
 *  and the CSS unit (e.g. "px").
 */
export const useGapParser = (gap: number | string | undefined): StackGap => {
  const stackGap: StackGap = {
    rowGap: {
      value: 0,
      unit: "px",
    },
    columnGap: {
      value: 0,
      unit: "px",
    },
  };

  if (gap === undefined || gap === "") {
    return stackGap;
  }

  if (typeof gap === "number") {
    stackGap.rowGap.value = gap;
    stackGap.columnGap.value = gap;
    return stackGap;
  }
  const splitGap = gap.split(" ");
  // If the array has more than two values, then return 0px.
  if (splitGap.length > 2) {
    return stackGap;
  }
  // If the array has two values, then parse each one.
  if (splitGap.length === 2) {
    stackGap.rowGap = _getValueUnitGap(splitGap[0]);
    stackGap.columnGap = _getValueUnitGap(splitGap[1]);
    return stackGap;
  }
  // Else, parse the numerical value and pass it as both the vertical and horizontal gap.
  const calculatedGap = _getValueUnitGap(gap);
  stackGap.rowGap = calculatedGap;
  stackGap.columnGap = calculatedGap;
  return stackGap;
};

export const useLengthParser = (
  length: number | string | undefined
): string => {
  if (length === undefined || length === "") {
    return "0px";
  }
  if (typeof length === "number") {
    return length.toString() + "px";
  }
  return length;
};

export const useAlignmentParser = (align?: string): string => {
  switch (align) {
    case "start":
      return "flex-start";
    case "end":
      return "flex-end";
    default:
      return align ?? "";
  }
};

// Helper function that takes a gap as a string and converts it into a { value, unit } representation.
const _getValueUnitGap = (gap: string) => {
  const numericalPart = parseFloat(gap);
  const numericalValue = isNaN(numericalPart) ? 0 : numericalPart;
  const numericalString = isNaN(numericalPart) ? "" : numericalPart.toString();
  const unitPart = gap.substring(numericalString.toString().length);
  return {
    value: numericalValue,
    unit: unitPart || "px",
  };
};
